// Driver Geolocation Hook
// Tracks the driver's location and sends updates to the server

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthStore } from '../store/auth.store';
import { useBatterySaver } from './useBatterySaver';

interface GeolocationState {
  isTracking: boolean;
  isSupported: boolean;
  hasPermission: boolean | null;
  currentPosition: GeolocationPosition | null;
  error: string | null;
  lastUpdate: Date | null;
  accuracy: number | null;
}

interface UseDriverGeolocationOptions {
  onPositionUpdate?: (position: GeolocationPosition) => void;
  sendToServer?: boolean;
  highAccuracy?: boolean;
}

interface UseDriverGeolocationReturn extends GeolocationState {
  startTracking: () => Promise<boolean>;
  stopTracking: () => void;
  requestPermission: () => Promise<boolean>;
  getCurrentPosition: () => Promise<GeolocationPosition | null>;
}

const STORAGE_KEY = 'trackfleet-driver-mode';

export function useDriverGeolocation(
  options: UseDriverGeolocationOptions = {}
): UseDriverGeolocationReturn {
  const { onPositionUpdate, sendToServer = true, highAccuracy = true } = options;
  const { user, accessToken } = useAuthStore();
  const { getRefreshIntervalMs } = useBatterySaver();

  const [state, setState] = useState<GeolocationState>({
    isTracking: false,
    isSupported: 'geolocation' in navigator,
    hasPermission: null,
    currentPosition: null,
    error: null,
    lastUpdate: null,
    accuracy: null,
  });

  const watchIdRef = useRef<number | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  // Check if geolocation permission is granted
  const checkPermission = useCallback(async (): Promise<boolean> => {
    if (!('permissions' in navigator)) {
      // Older browsers - try to get position to check
      return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          () => resolve(true),
          () => resolve(false),
          { timeout: 5000 }
        );
      });
    }

    try {
      const result = await navigator.permissions.query({ name: 'geolocation' });
      const granted = result.state === 'granted';
      setState((prev) => ({ ...prev, hasPermission: granted }));
      return granted;
    } catch {
      return false;
    }
  }, []);

  // Request geolocation permission
  const requestPermission = useCallback(async (): Promise<boolean> => {
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setState((prev) => ({
            ...prev,
            hasPermission: true,
            currentPosition: position,
            lastUpdate: new Date(),
            accuracy: position.coords.accuracy,
          }));
          resolve(true);
        },
        (error) => {
          setState((prev) => ({
            ...prev,
            hasPermission: false,
            error: getErrorMessage(error),
          }));
          resolve(false);
        },
        { enableHighAccuracy: highAccuracy, timeout: 10000 }
      );
    });
  }, [highAccuracy]);

  // Get current position once
  const getCurrentPosition = useCallback(async (): Promise<GeolocationPosition | null> => {
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setState((prev) => ({
            ...prev,
            currentPosition: position,
            lastUpdate: new Date(),
            accuracy: position.coords.accuracy,
            error: null,
          }));
          resolve(position);
        },
        (error) => {
          setState((prev) => ({ ...prev, error: getErrorMessage(error) }));
          resolve(null);
        },
        { enableHighAccuracy: highAccuracy, timeout: 10000 }
      );
    });
  }, [highAccuracy]);

  // Send position to server via HTTP
  const sendPositionToServer = useCallback(
    (position: GeolocationPosition) => {
      if (!sendToServer || !user) return;

      const payload = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        altitude: position.coords.altitude,
        heading: position.coords.heading,
        speed: position.coords.speed,
        deviceName: user.firstName ? `${user.firstName} ${user.lastName || ''} (Téléphone)`.trim() : undefined,
      };

      // Send via HTTP POST
      fetch('/api/drivers/position', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      }).catch((err) => console.error('Failed to send position:', err));
    },
    [sendToServer, user, accessToken]
  );

  // Notify server when stopping tracking
  const notifyStopTracking = useCallback(() => {
    if (!accessToken) return;

    fetch('/api/drivers/position', {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }).catch((err) => console.error('Failed to notify stop:', err));
  }, [accessToken]);

  // Handle position update
  const handlePositionUpdate = useCallback(
    (position: GeolocationPosition) => {
      setState((prev) => ({
        ...prev,
        currentPosition: position,
        lastUpdate: new Date(),
        accuracy: position.coords.accuracy,
        error: null,
      }));

      // Call custom handler
      onPositionUpdate?.(position);

      // Send to server
      sendPositionToServer(position);
    },
    [onPositionUpdate, sendPositionToServer]
  );

  // Handle position error
  const handlePositionError = useCallback((error: GeolocationPositionError) => {
    setState((prev) => ({
      ...prev,
      error: getErrorMessage(error),
    }));
  }, []);

  // Start tracking
  const startTracking = useCallback(async (): Promise<boolean> => {
    if (!state.isSupported) {
      setState((prev) => ({
        ...prev,
        error: 'La géolocalisation n\'est pas supportée par ce navigateur',
      }));
      return false;
    }

    // Check/request permission
    const hasPermission = await requestPermission();
    if (!hasPermission) {
      return false;
    }

    // Get refresh interval from battery saver settings
    const intervalMs = getRefreshIntervalMs();

    // Start watching position
    watchIdRef.current = navigator.geolocation.watchPosition(
      handlePositionUpdate,
      handlePositionError,
      {
        enableHighAccuracy: highAccuracy,
        timeout: 30000,
        maximumAge: intervalMs,
      }
    );

    setState((prev) => ({ ...prev, isTracking: true, error: null }));

    // Store tracking state
    localStorage.setItem(STORAGE_KEY, 'active');

    // Vibrate to confirm
    if (navigator.vibrate) {
      navigator.vibrate([100, 50, 100]);
    }

    return true;
  }, [
    state.isSupported,
    requestPermission,
    getRefreshIntervalMs,
    handlePositionUpdate,
    handlePositionError,
    highAccuracy,
  ]);

  // Stop tracking
  const stopTracking = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    // Notify server that tracking stopped
    notifyStopTracking();

    setState((prev) => ({ ...prev, isTracking: false }));
    localStorage.removeItem(STORAGE_KEY);

    // Vibrate to confirm
    if (navigator.vibrate) {
      navigator.vibrate(100);
    }
  }, [notifyStopTracking]);

  // Check for active tracking on mount
  useEffect(() => {
    const wasTracking = localStorage.getItem(STORAGE_KEY) === 'active';
    if (wasTracking) {
      startTracking();
    }

    checkPermission();

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, []);

  return {
    ...state,
    startTracking,
    stopTracking,
    requestPermission,
    getCurrentPosition,
  };
}

// Helper to get user-friendly error message
function getErrorMessage(error: GeolocationPositionError): string {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return 'Permission de géolocalisation refusée. Activez-la dans les paramètres.';
    case error.POSITION_UNAVAILABLE:
      return 'Position indisponible. Vérifiez que le GPS est activé.';
    case error.TIMEOUT:
      return 'Délai d\'attente dépassé. Réessayez.';
    default:
      return 'Erreur de géolocalisation inconnue.';
  }
}

export default useDriverGeolocation;
